import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import PageHeader from './components/PageHeader';
import FieldTypeIconPicker from './components/FieldTypeIconPicker';
import { apiFetch, API_BASE_URL } from './utils/api';

const FALLBACK_DOCUMENT_TYPES = [
  { value: 'invoice', label: 'Invoice' },
  { value: 'quote', label: 'Quote' },
  { value: 'job', label: 'Job' },
  { value: 'purchase_order', label: 'Purchase Order' },
  { value: 'customer', label: 'Customer' },
  { value: 'compliance_forms', label: 'Compliance Forms' },
];

const FALLBACK_MERGE_FIELDS = {
  invoice: [
    { field: 'invoice_number', description: 'Invoice Number' },
    { field: 'invoice_date', description: 'Invoice Date' },
  ],
  customer: [
    { field: 'customer_name', description: 'Customer Name' },
  ],
  financial: [
    { field: 'subtotal', description: 'Subtotal before VAT' },
    { field: 'vat_amount', description: 'VAT amount' },
    { field: 'total', description: 'Total including VAT' },
  ],
};

const DOCUMENT_FIELD_GROUPS = {
  invoice: ['invoice', 'customer', 'site', 'job', 'quote', 'company', 'financial'],
  quote: ['quote', 'customer', 'site', 'job', 'company', 'financial'],
  job: ['job', 'customer', 'site', 'engineer', 'appointment', 'company'],
  purchase_order: ['purchase_order', 'supplier', 'product', 'job', 'company', 'financial'],
  customer: ['customer', 'site', 'contact', 'company', 'financial'],
};

const DEFAULT_FONT_OPTIONS = [
  { value: 'helvetica', label: 'Helvetica', css: 'Arial, Helvetica, sans-serif' },
  { value: 'times', label: 'Times', css: '"Times New Roman", Times, serif' },
  { value: 'courier', label: 'Courier', css: '"Courier New", Courier, monospace' },
];

const DEFAULT_TEXT_COLOUR_SWATCHES = [
  { value: '#000000', label: 'Black' },
  { value: '#444444', label: 'Dark grey' },
  { value: '#666666', label: 'Grey' },
  { value: '#999999', label: 'Light grey' },
  { value: '#ffffff', label: 'White' },
  { value: '#c00000', label: 'Dark red' },
  { value: '#ff0000', label: 'Red' },
  { value: '#ffc000', label: 'Gold' },
  { value: '#ffff00', label: 'Yellow' },
  { value: '#92d050', label: 'Light green' },
  { value: '#00b050', label: 'Green' },
  { value: '#00b0f0', label: 'Light blue' },
  { value: '#0070c0', label: 'Blue' },
  { value: '#002060', label: 'Navy' },
  { value: '#7030a0', label: 'Purple' },
  { value: '#e46c0a', label: 'Orange' },
];

const ALIGN_OPTIONS = [
  { value: 'left', label: 'Left' },
  { value: 'center', label: 'Centre' },
  { value: 'right', label: 'Right' },
];

const V_ALIGN_OPTIONS = [
  { value: 'top', label: 'Top' },
  { value: 'middle', label: 'Middle' },
  { value: 'bottom', label: 'Bottom' },
];

const FIELD_TYPES = [
  { value: 'text', label: 'Text' },
  { value: 'multiline', label: 'Multi-line Text' },
  { value: 'number', label: 'Number' },
  { value: 'currency', label: 'Currency' },
  { value: 'checkbox', label: 'Checkbox / Tick' },
  { value: 'yes_no', label: 'Yes / No' },
  { value: 'dropdown', label: 'Dropdown' },
  { value: 'multi_select', label: 'Multi-select' },
  { value: 'date', label: 'Date' },
  { value: 'time', label: 'Time' },
  { value: 'datetime', label: 'Date + Time' },
  { value: 'signature', label: 'Signature' },
  { value: 'photo', label: 'Photo' },
  { value: 'initials', label: 'Initials' },
  { value: 'gps_location', label: 'GPS Location' },
  { value: 'asset_lookup', label: 'Asset Lookup' },
  { value: 'part_lookup', label: 'Part Lookup' },
];

const SNAP_OPTIONS = [
  { value: 0.25, label: '0.25%' },
  { value: 0.5, label: '0.5%' },
  { value: 1, label: '1%' },
];

function authHeaders(extra = {}) {
  const token = localStorage.getItem('doorbase_token');
  return token ? { ...extra, Authorization: `Bearer ${token}` } : extra;
}

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function snapValue(value, step, enabled) {
  if (!enabled || !step) return value;
  return Math.round(value / step) * step;
}

function validHexColour(value, fallback = '#111827') {
  const text = String(value || '').trim();
  const prefixed = text.startsWith('#') ? text : `#${text}`;
  if (/^#[0-9a-f]{6}$/i.test(prefixed)) return prefixed;
  if (/^#[0-9a-f]{3}$/i.test(prefixed)) {
    return `#${prefixed.slice(1).split('').map((char) => char + char).join('')}`;
  }
  return fallback;
}

function titleFromKey(key) {
  return String(key || '').replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
}

function normaliseMergeField(item, groupKey) {
  if (!item || item.type === 'loop' || !item.field) return null;
  const fieldType = normaliseFieldType(item.fieldType || item.field_type);
  return {
    value: item.field,
    label: item.description || titleFromKey(item.field),
    groupKey,
    groupLabel: titleFromKey(groupKey),
    sample: sampleValueForField(item.field, fieldType),
    fieldType,
  };
}

function groupedFieldsForDocumentType(documentType, mergeFields) {
  const source = mergeFields && Object.keys(mergeFields).length ? mergeFields : FALLBACK_MERGE_FIELDS;
  const preferredGroups = DOCUMENT_FIELD_GROUPS[documentType];
  const orderedGroups = preferredGroups || Object.keys(source);

  return orderedGroups
    .map((groupKey) => ({
      key: groupKey,
      label: titleFromKey(groupKey),
      fields: (source[groupKey] || [])
        .map((item) => normaliseMergeField(item, groupKey))
        .filter(Boolean),
    }))
    .filter((group) => group.fields.length > 0);
}

function flattenFieldGroups(groups) {
  return groups.flatMap((group) => group.fields);
}

function fontCssFamily(fontFamily) {
  return DEFAULT_FONT_OPTIONS.find((font) => font.value === fontFamily)?.css || DEFAULT_FONT_OPTIONS[0].css;
}

function normaliseFieldType(value) {
  return FIELD_TYPES.some((type) => type.value === value) ? value : 'text';
}

function sampleValueForField(field, fieldType = 'text') {
  const lower = String(field || '').toLowerCase();
  if (fieldType === 'number') return '42';
  if (fieldType === 'currency') return '125.00';
  if (fieldType === 'time') return '14:30';
  if (fieldType === 'datetime') return '19/06/2026 14:30';
  if (fieldType === 'yes_no') return 'Yes';
  if (fieldType === 'dropdown') return 'Pass';
  if (fieldType === 'multi_select') return 'Option 1, Option 2';
  if (fieldType === 'initials') return 'AT';
  if (fieldType === 'gps_location') return '51.4545, -2.5879';
  if (fieldType === 'asset_lookup') return 'Main Entrance Door - EQ-001';
  if (fieldType === 'part_lookup') return 'ADG-ROLLER-01 - Roller Kit';
  if (lower.includes('signature')) return 'Signature';
  if (lower.includes('photo') || lower.includes('image')) return 'Photo';
  if (lower.includes('initial')) return 'AT';
  if (lower.includes('gps') || lower.includes('location_stamp')) return '51.4545, -2.5879';
  if (lower.includes('asset') || lower.includes('equipment')) return 'Main Entrance Door - EQ-001';
  if (lower.includes('part') || lower.includes('product')) return 'ADG-ROLLER-01 - Roller Kit';
  if (lower.startsWith('is_') || lower.includes('checked') || lower.includes('yes_no')) return 'Yes';
  if (lower.includes('total') && !lower.includes('profit')) return '1,500.00';
  if (lower.includes('subtotal')) return '1,250.00';
  if (lower.includes('vat')) return '250.00';
  if (lower.includes('date')) return '19/06/2026';
  if (lower.includes('email')) return 'name@example.com';
  if (lower.includes('phone') || lower.includes('mobile')) return '0117 555 0182';
  if (lower.includes('address')) return 'Unit 8, Trade Park\nBristol\nBS1 4AA';
  if (lower.includes('customer') && lower.includes('name')) return 'Bristol Access Systems Ltd';
  if (lower.includes('supplier') && lower.includes('name')) return 'Industrial Door Supplies Ltd';
  if (lower.includes('engineer') && lower.includes('name')) return 'Alex Taylor';
  if (lower.includes('invoice')) return 'INV-1042';
  if (lower.includes('quote')) return 'Q-2034';
  if (lower.includes('job')) return 'JOB-2251';
  if (lower.includes('po_') || lower.includes('purchase')) return 'PO-7781';
  if (lower.includes('status')) return 'Completed';
  if (lower.includes('description') || lower.includes('notes')) return 'Preview text';
  return titleFromKey(field);
}

function formatPreviewValue(value, fieldType) {
  const text = String(value ?? '');
  if (fieldType === 'currency') {
    const amount = Number(text.replace(/[^0-9.-]/g, ''));
    return Number.isFinite(amount)
      ? new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(amount)
      : text;
  }
  if (fieldType === 'number') {
    const number = Number(text.replace(/,/g, ''));
    return Number.isFinite(number) ? new Intl.NumberFormat('en-GB').format(number) : text;
  }
  if (fieldType === 'yes_no') {
    if (!text.trim()) return '';
    return ['true', 'yes', 'y', '1', 'checked', 'complete', 'completed', 'pass', 'passed'].includes(text.trim().toLowerCase()) ? 'Yes' : 'No';
  }
  if (fieldType === 'gps_location') {
    return text.replace(/\s+/g, ' ').trim();
  }
  return text;
}

function normaliseFields(fields) {
  if (!Array.isArray(fields)) return [];
  return fields.map((field, index) => ({
    id: field.id || `field-${Date.now()}-${index}`,
    field: field.field || 'invoice_number',
    label: field.label || field.field || 'Field',
    fieldType: normaliseFieldType(field.fieldType || field.type),
    page: 1,
    x: Number.isFinite(Number(field.x)) ? Number(field.x) : 0.1,
    y: Number.isFinite(Number(field.y)) ? Number(field.y) : 0.1,
    width: Number.isFinite(Number(field.width)) ? Number(field.width) : 0.2,
    height: Number.isFinite(Number(field.height)) ? Number(field.height) : 0.035,
    fontSize: Number.isFinite(Number(field.fontSize)) ? Number(field.fontSize) : 10,
    textOffsetY: Number.isFinite(Number(field.textOffsetY)) ? Number(field.textOffsetY) : 0,
    fontFamily: DEFAULT_FONT_OPTIONS.some((font) => font.value === field.fontFamily) ? field.fontFamily : 'helvetica',
    bold: field.bold === true,
    color: validHexColour(field.color),
    align: ['left', 'center', 'right'].includes(field.align) ? field.align : 'left',
    vAlign: ['top', 'middle', 'bottom'].includes(field.vAlign) ? field.vAlign : 'top',
  }));
}

function formatBytes(size) {
  if (!size) return '0 KB';
  if (size < 1024 * 1024) return `${Math.round(size / 1024)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

function PdfOverlayTemplatesPage() {
  const [searchParams] = useSearchParams();
  const pageRef = useRef(null);
  const panSurfaceRef = useRef(null);
  const interactionRef = useRef(null);
  const panRef = useRef(null);
  const [templates, setTemplates] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [selected, setSelected] = useState(null);
  const [fields, setFields] = useState([]);
  const [selectedFieldId, setSelectedFieldId] = useState('');
  const [newFieldType, setNewFieldType] = useState('text');
  const [documentType, setDocumentType] = useState(searchParams.get('type') || 'invoice');
  const [documentTypes, setDocumentTypes] = useState(FALLBACK_DOCUMENT_TYPES);
  const [mergeFields, setMergeFields] = useState(FALLBACK_MERGE_FIELDS);
  const [designSettings, setDesignSettings] = useState({
    text_colours: DEFAULT_TEXT_COLOUR_SWATCHES,
    font_options: DEFAULT_FONT_OPTIONS,
  });
  const [zoom, setZoom] = useState(1);
  const [pagePixelWidth, setPagePixelWidth] = useState(820);
  const [pdfPageWidthPoints, setPdfPageWidthPoints] = useState(595.28);
  const [pdfPageAspectRatio, setPdfPageAspectRatio] = useState('210 / 297');
  const [isPanning, setIsPanning] = useState(false);
  const [snapEnabled, setSnapEnabled] = useState(true);
  const [snapStepPercent, setSnapStepPercent] = useState(0.5);
  const [showSampleValues, setShowSampleValues] = useState(true);
  const [pdfImageUrl, setPdfImageUrl] = useState('');
  const [name, setName] = useState('');
  const [file, setFile] = useState(null);
  const [isDefault, setIsDefault] = useState(true);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const [resolvedTemplate, setResolvedTemplate] = useState(null);

  const selectedField = fields.find((field) => field.id === selectedFieldId) || null;
  const requestedTemplateId = searchParams.get('template') || '';
  const requestedDocumentType = searchParams.get('type') || '';
  const snapStep = snapStepPercent / 100;
  const pdfPointScale = pagePixelWidth / pdfPageWidthPoints;
  const groupedFields = useMemo(() => groupedFieldsForDocumentType(documentType, mergeFields), [documentType, mergeFields]);
  const availableFields = useMemo(() => flattenFieldGroups(groupedFields), [groupedFields]);
  const textColourSwatches = Array.isArray(designSettings.text_colours) && designSettings.text_colours.length
    ? designSettings.text_colours
    : DEFAULT_TEXT_COLOUR_SWATCHES;
  const fontOptions = Array.isArray(designSettings.font_options) && designSettings.font_options.length
    ? designSettings.font_options
    : DEFAULT_FONT_OPTIONS;

  const sampleData = useMemo(() => {
    return availableFields.reduce((acc, field) => {
      acc[field.value] = field.sample;
      return acc;
    }, {});
  }, [availableFields]);

  const loadDocumentTypes = useCallback(async () => {
    try {
      const res = await apiFetch('/api/documents/document-types');
      const data = await res.json();
      const rows = Array.isArray(data) ? data : [];
      if (rows.length) {
        setDocumentTypes(rows.map((row) => ({
          value: row.value || row.key,
          label: row.label || titleFromKey(row.value || row.key),
        })).filter((row) => row.value));
      }
    } catch {
      setDocumentTypes(FALLBACK_DOCUMENT_TYPES);
    }
  }, []);

  const loadMergeFields = useCallback(async () => {
    try {
      const res = await apiFetch('/api/documents/merge-fields');
      const data = await res.json();
      if (data && typeof data === 'object' && !Array.isArray(data)) {
        setMergeFields(data);
      }
    } catch {
      setMergeFields(FALLBACK_MERGE_FIELDS);
    }
  }, []);

  const loadDesignSettings = useCallback(async () => {
    try {
      const res = await apiFetch('/api/company-settings/document-design');
      const data = await res.json();
      if (data && typeof data === 'object') {
        setDesignSettings({
          text_colours: Array.isArray(data.text_colours) && data.text_colours.length
            ? data.text_colours
            : DEFAULT_TEXT_COLOUR_SWATCHES,
          font_options: Array.isArray(data.font_options) && data.font_options.length
            ? data.font_options
            : DEFAULT_FONT_OPTIONS,
        });
      }
    } catch {
      setDesignSettings({ text_colours: DEFAULT_TEXT_COLOUR_SWATCHES, font_options: DEFAULT_FONT_OPTIONS });
    }
  }, []);

  const loadTemplates = useCallback(async () => {
    setError('');
    try {
      const res = await apiFetch(`/api/documents/pdf-templates?type=${encodeURIComponent(documentType)}`);
      const data = await res.json();
      const rows = Array.isArray(data) ? data : [];
      const mergedRows = resolvedTemplate && resolvedTemplate.document_type === documentType && !rows.some((row) => row.id === resolvedTemplate.id)
        ? [resolvedTemplate, ...rows]
        : rows;
      setTemplates(mergedRows);
      setSelectedId((current) => {
        if (resolvedTemplate?.id && mergedRows.some((row) => row.id === resolvedTemplate.id)) return resolvedTemplate.id;
        if (requestedTemplateId && mergedRows.some((row) => row.id === requestedTemplateId)) return requestedTemplateId;
        if (mergedRows.some((row) => row.id === current)) return current;
        return mergedRows[0]?.id || '';
      });
    } catch (err) {
      setError(err.message || 'Failed to load PDF templates.');
    }
  }, [documentType, requestedTemplateId, resolvedTemplate]);

  useEffect(() => {
    loadDocumentTypes();
    loadMergeFields();
    loadDesignSettings();
  }, [loadDocumentTypes, loadMergeFields, loadDesignSettings]);

  useEffect(() => {
    if (requestedDocumentType && requestedDocumentType !== documentType) {
      setDocumentType(requestedDocumentType);
    }
  }, [requestedDocumentType, documentType]);

  useEffect(() => {
    let active = true;

    async function resolveRequestedTemplate() {
      if (!requestedTemplateId) {
        setResolvedTemplate(null);
        return;
      }

      try {
        const res = await apiFetch(`/api/documents/pdf-templates/${requestedTemplateId}`);
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Failed to load selected PDF template.');
        if (!active) return;
        setResolvedTemplate(data);
        if (data.document_type && data.document_type !== documentType) {
          setDocumentType(data.document_type);
        }
        setSelectedId(data.id);
      } catch (err) {
        if (active) setError(err.message || 'Failed to load selected PDF template.');
      }
    }

    resolveRequestedTemplate();
    return () => {
      active = false;
    };
  }, [requestedTemplateId, documentType]);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  useEffect(() => {
    const next = templates.find((template) => template.id === selectedId) || null;
    setSelected(next);
    setFields(normaliseFields(next?.fields));
    setName(next?.name || '');
    setIsDefault(next?.is_default !== false);
    setSelectedFieldId('');
  }, [selectedId, templates]);

  useEffect(() => {
    let active = true;

    async function loadPdf() {
      if (!selected?.id) {
        setPdfImageUrl('');
        return;
      }

      try {
        const res = await fetch(`${API_BASE_URL}/api/documents/pdf-templates/${selected.id}/file`, {
          headers: authHeaders(),
        });
        if (!res.ok) throw new Error('PDF file could not be loaded.');
        const arrayBuffer = await res.arrayBuffer();
        const [pdfjsLib, pdfWorker] = await Promise.all([
          import('pdfjs-dist/legacy/build/pdf'),
          import('pdfjs-dist/legacy/build/pdf.worker.entry'),
        ]);
        pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorker.default || pdfWorker;
        const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
        const page = await pdf.getPage(1);
        const unitViewport = page.getViewport({ scale: 1 });
        const renderViewport = page.getViewport({ scale: 2 });
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        canvas.width = Math.ceil(renderViewport.width);
        canvas.height = Math.ceil(renderViewport.height);
        await page.render({ canvasContext: context, viewport: renderViewport }).promise;
        const imageUrl = canvas.toDataURL('image/png');
        await pdf.destroy();

        if (active) {
          setPdfImageUrl(imageUrl);
          setPdfPageWidthPoints(unitViewport.width || 595.28);
          setPdfPageAspectRatio(`${unitViewport.width || 210} / ${unitViewport.height || 297}`);
        }
      } catch (err) {
        if (active) setError(err.message || 'PDF file could not be loaded.');
      }
    }

    loadPdf();

    return () => {
      active = false;
    };
  }, [selected?.id]);

  useEffect(() => {
    if (!pageRef.current) return undefined;
    const pageElement = pageRef.current;
    const measure = () => {
      const rect = pageElement.getBoundingClientRect();
      if (rect.width) setPagePixelWidth(rect.width);
    };
    measure();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }
    const observer = new ResizeObserver(measure);
    observer.observe(pageElement);
    return () => observer.disconnect();
  }, [zoom, selected?.id]);

  async function handleUpload(event) {
    event.preventDefault();
    if (!file) {
      setError('Choose a PDF first.');
      return;
    }

    setLoading(true);
    setError('');
    setStatus('');
    try {
      const formData = new FormData();
      formData.append('name', name || file.name.replace(/\.pdf$/i, ''));
      formData.append('document_type', documentType);
      formData.append('is_default', isDefault ? 'true' : 'false');
      formData.append('file', file);

      const res = await fetch(`${API_BASE_URL}/api/documents/pdf-templates`, {
        method: 'POST',
        headers: authHeaders(),
        body: formData,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Upload failed.');
      setName(data.name || '');
      setIsDefault(data.is_default !== false);
      setFile(null);
      setStatus('PDF template uploaded.');
      await loadTemplates();
      setSelectedId(data.id);
    } catch (err) {
      setError(err.message || 'Upload failed.');
    } finally {
      setLoading(false);
    }
  }

  function addField() {
    const fieldDef = availableFields.find((field) => field.fieldType === newFieldType)
      || availableFields[0]
      || normaliseMergeField(FALLBACK_MERGE_FIELDS.invoice[0], 'invoice');
    if (!fieldDef) return;
    const newField = {
      id: `field-${Date.now()}`,
      field: fieldDef.value,
      label: fieldDef.label,
      page: 1,
      x: 0.12,
      y: 0.12,
      width: 0.25,
      height: 0.04,
      fontSize: 10,
      textOffsetY: 0,
      fontFamily: 'helvetica',
      bold: false,
      color: '#111827',
      fieldType: newFieldType || fieldDef.fieldType || 'text',
      align: 'left',
      vAlign: 'top',
    };
    setFields((current) => [...current, newField]);
    setSelectedFieldId(newField.id);
  }

  function updateField(id, patch) {
    setFields((current) => current.map((field) => (field.id === id ? { ...field, ...patch } : field)));
  }

  function removeField(id) {
    setFields((current) => current.filter((field) => field.id !== id));
    if (selectedFieldId === id) setSelectedFieldId('');
  }

  function startDrag(event, field) {
    if (!pageRef.current) return;
    const rect = pageRef.current.getBoundingClientRect();
    event.currentTarget.setPointerCapture(event.pointerId);
    interactionRef.current = {
      mode: 'drag',
      id: field.id,
      rect,
      startX: event.clientX,
      startY: event.clientY,
      originalX: field.x,
      originalY: field.y,
    };
    setSelectedFieldId(field.id);
  }

  function startResize(event, field, handle) {
    if (!pageRef.current) return;
    event.stopPropagation();
    const rect = pageRef.current.getBoundingClientRect();
    event.currentTarget.setPointerCapture(event.pointerId);
    interactionRef.current = {
      mode: 'resize',
      id: field.id,
      handle,
      rect,
      startX: event.clientX,
      startY: event.clientY,
      originalX: field.x,
      originalY: field.y,
      originalWidth: field.width,
      originalHeight: field.height,
    };
    setSelectedFieldId(field.id);
  }

  function handlePointerMove(event) {
    const interaction = interactionRef.current;
    if (!interaction) return;
    const dx = (event.clientX - interaction.startX) / interaction.rect.width;
    const dy = (event.clientY - interaction.startY) / interaction.rect.height;
    const field = fields.find((item) => item.id === interaction.id);
    if (!field) return;

    if (interaction.mode === 'drag') {
      const nextX = snapValue(interaction.originalX + dx, snapStep, snapEnabled);
      const nextY = snapValue(interaction.originalY + dy, snapStep, snapEnabled);
      updateField(interaction.id, {
        x: clamp(nextX, 0, 1 - field.width),
        y: clamp(nextY, 0, 1 - field.height),
      });
      return;
    }

    const minWidth = 0.025;
    const minHeight = 0.012;
    let nextX = interaction.originalX;
    let nextY = interaction.originalY;
    let nextWidth = interaction.originalWidth;
    let nextHeight = interaction.originalHeight;

    if (interaction.handle.includes('e')) {
      nextWidth = interaction.originalWidth + dx;
    }
    if (interaction.handle.includes('s')) {
      nextHeight = interaction.originalHeight + dy;
    }
    if (interaction.handle.includes('w')) {
      nextX = interaction.originalX + dx;
      nextWidth = interaction.originalWidth - dx;
    }
    if (interaction.handle.includes('n')) {
      nextY = interaction.originalY + dy;
      nextHeight = interaction.originalHeight - dy;
    }

    nextWidth = clamp(nextWidth, minWidth, 1 - nextX);
    nextHeight = clamp(nextHeight, minHeight, 1 - nextY);
    nextX = clamp(nextX, 0, interaction.originalX + interaction.originalWidth - minWidth);
    nextY = clamp(nextY, 0, interaction.originalY + interaction.originalHeight - minHeight);
    nextWidth = clamp(nextWidth, minWidth, 1 - nextX);
    nextHeight = clamp(nextHeight, minHeight, 1 - nextY);
    nextX = snapValue(nextX, snapStep, snapEnabled);
    nextY = snapValue(nextY, snapStep, snapEnabled);
    nextWidth = snapValue(nextWidth, snapStep, snapEnabled);
    nextHeight = snapValue(nextHeight, snapStep, snapEnabled);
    nextX = clamp(nextX, 0, 1 - minWidth);
    nextY = clamp(nextY, 0, 1 - minHeight);
    nextWidth = clamp(nextWidth, minWidth, 1 - nextX);
    nextHeight = clamp(nextHeight, minHeight, 1 - nextY);

    updateField(interaction.id, {
      x: nextX,
      y: nextY,
      width: nextWidth,
      height: nextHeight,
    });
  }

  function stopDrag() {
    interactionRef.current = null;
  }

  function changeZoom(value) {
    setZoom(clamp(value, 0.5, 2.5));
  }

  function startPan(event) {
    if (zoom <= 1 || !panSurfaceRef.current) return;
    if (event.button !== 0) return;
    if (event.target.closest('[data-field-box="true"]')) return;
    const surface = panSurfaceRef.current;
    surface.setPointerCapture(event.pointerId);
    panRef.current = {
      startX: event.clientX,
      startY: event.clientY,
      scrollLeft: surface.scrollLeft,
      scrollTop: surface.scrollTop,
    };
    setIsPanning(true);
  }

  function handlePanMove(event) {
    const pan = panRef.current;
    if (!panSurfaceRef.current || !pan) return;
    panSurfaceRef.current.scrollLeft = pan.scrollLeft - (event.clientX - pan.startX);
    panSurfaceRef.current.scrollTop = pan.scrollTop - (event.clientY - pan.startY);
  }

  function stopPan() {
    panRef.current = null;
    setIsPanning(false);
  }

  function handleFieldKeyDown(event, field) {
    const keyMap = {
      ArrowLeft: [-1, 0],
      ArrowRight: [1, 0],
      ArrowUp: [0, -1],
      ArrowDown: [0, 1],
    };
    const direction = keyMap[event.key];
    if (!direction) return;
    event.preventDefault();
    const step = event.shiftKey ? 0.001 : snapStep;
    updateField(field.id, {
      x: clamp(field.x + (direction[0] * step), 0, 1 - field.width),
      y: clamp(field.y + (direction[1] * step), 0, 1 - field.height),
    });
  }

  async function saveTemplate() {
    if (!selected) return;
    setLoading(true);
    setError('');
    setStatus('');
    try {
      const res = await apiFetch(`/api/documents/pdf-templates/${selected.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name.trim() || selected.name,
          document_type: documentType,
          is_default: isDefault,
          fields: normaliseFields(fields),
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Template save failed.');
      setTemplates((current) => {
        if (data.document_type !== documentType) return current.filter((template) => template.id !== data.id);
        return current.map((template) => (template.id === data.id ? data : template));
      });
      setSelected(data);
      setSelectedId(data.id);
      setName(data.name || '');
      setIsDefault(data.is_default !== false);
      setStatus('PDF template saved to library.');
    } catch (err) {
      setError(err.message || 'Template save failed.');
    } finally {
      setLoading(false);
    }
  }

  async function deleteTemplate() {
    if (!selected) return;
    if (!window.confirm(`Delete ${selected.name || 'this PDF template'}? This cannot be undone.`)) return;
    setLoading(true);
    setError('');
    setStatus('');
    try {
      const res = await apiFetch(`/api/documents/pdf-templates/${selected.id}`, { method: 'DELETE' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Template delete failed.');
      const remaining = templates.filter((template) => template.id !== selected.id);
      setTemplates(remaining);
      setSelectedId(remaining[0]?.id || '');
      setStatus('PDF template deleted from library.');
    } catch (err) {
      setError(err.message || 'Template delete failed.');
    } finally {
      setLoading(false);
    }
  }

  async function generateSample() {
    if (!selected) return;
    setLoading(true);
    setError('');
    setStatus('');
    try {
      const res = await fetch(`${API_BASE_URL}/api/documents/pdf-templates/${selected.id}/generate`, {
        method: 'POST',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          data: sampleData,
          fields: normaliseFields(fields),
          filename: `${selected.name || documentType}-sample.pdf`,
        }),
      });
      const data = res.ok ? null : await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'PDF generation failed.');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      window.setTimeout(() => URL.revokeObjectURL(url), 60000);
      setStatus('Sample PDF generated.');
    } catch (err) {
      setError(err.message || 'PDF generation failed.');
    } finally {
      setLoading(false);
    }
  }

  function handleFieldNameChange(value) {
    const option = availableFields.find((item) => item.value === value);
    if (!selectedField) return;
    updateField(selectedField.id, { field: value, label: option?.label || value, fieldType: option?.fieldType || selectedField.fieldType || 'text' });
  }

  function previewTextForField(field) {
    if (!showSampleValues) return field.label;
    const value = sampleData[field.field];
    return value === undefined || value === null ? field.label : String(value);
  }

  function previewContentForField(field) {
    const fieldType = normaliseFieldType(field.fieldType);
    if (fieldType === 'checkbox') {
      const value = previewTextForField(field).toLowerCase();
      const checked = ['true', 'yes', 'y', '1', 'checked', 'complete', 'completed'].some((token) => value.includes(token));
      return checked ? '✓' : '';
    }
    if (fieldType === 'signature') return showSampleValues ? 'Signature' : field.label;
    if (fieldType === 'photo') return showSampleValues ? 'Photo' : field.label;
    return formatPreviewValue(previewTextForField(field), fieldType);
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title="PDF Overlay Templates"
        subtitle="Fixed PDF layouts with typed fields for document and mobile form output"
        backTo="/design-hub"
        backLabel="Design Hub"
        sticky
      />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-5">
        {(error || status) && (
          <div className={`rounded-md border px-4 py-3 text-sm ${error ? 'border-red-200 bg-red-50 text-red-700' : 'border-green-200 bg-green-50 text-green-700'}`}>
            {error || status}
          </div>
        )}

        <section className="grid grid-cols-1 lg:grid-cols-[320px_minmax(0,1fr)_300px] gap-5 items-start">
          <aside className="bg-white border border-gray-200 rounded-lg shadow-sm p-4 space-y-4">
            <form onSubmit={handleUpload} className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Function / Library</label>
                <select
                  value={documentType}
                  onChange={(event) => setDocumentType(event.target.value)}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                >
                  {documentTypes.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Template Name</label>
                <input
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                  placeholder="Invoice PDF"
                />
              </div>
              <label className="flex items-center gap-2 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm font-semibold text-gray-700">
                <input
                  type="checkbox"
                  checked={isDefault}
                  onChange={(event) => setIsDefault(event.target.checked)}
                />
                Default for library
              </label>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">PDF File</label>
                <input
                  type="file"
                  accept="application/pdf,.pdf"
                  onChange={(event) => setFile(event.target.files?.[0] || null)}
                  className="w-full text-sm"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full rounded-md bg-[#082547] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0a2d5c] disabled:opacity-50"
              >
                Upload PDF
              </button>
            </form>

            <div className="border-t border-gray-200 pt-4">
              <label className="block text-xs font-semibold text-gray-600 mb-1">Library</label>
              <select
                value={selectedId}
                onChange={(event) => setSelectedId(event.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              >
                <option value="">No PDF template</option>
                {templates.map((template) => (
                  <option key={template.id} value={template.id}>{template.name}</option>
                ))}
              </select>
              {selected && (
                <dl className="mt-3 grid grid-cols-2 gap-2 text-xs text-gray-600">
                  <dt className="font-semibold">File</dt>
                  <dd className="truncate text-right">{selected.original_filename}</dd>
                  <dt className="font-semibold">Size</dt>
                  <dd className="text-right">{formatBytes(selected.file_size)}</dd>
                  <dt className="font-semibold">Fields</dt>
                  <dd className="text-right">{fields.length}</dd>
                </dl>
              )}
            </div>

            <div className="border-t border-gray-200 pt-4">
              <FieldTypeIconPicker
                label="Add Field Box"
                value={newFieldType}
                options={FIELD_TYPES}
                onChange={setNewFieldType}
              />
              <button
                type="button"
                onClick={addField}
                disabled={!selected}
                className="mt-3 flex h-12 w-12 items-center justify-center rounded-md border border-gray-300 bg-white text-2xl font-semibold leading-none text-gray-800 hover:bg-gray-50 disabled:opacity-50"
                title="Add selected field"
                aria-label="Add selected field"
              >
                +
              </button>
            </div>
          </aside>

          <section className="bg-gray-200 rounded-lg border border-gray-300 p-3 sm:p-5">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => changeZoom(zoom - 0.1)}
                  className="h-9 w-9 rounded-md border border-gray-400 bg-white text-lg font-semibold text-gray-800 hover:bg-gray-50"
                  title="Zoom out"
                >
                  -
                </button>
                <input
                  type="range"
                  min="50"
                  max="250"
                  step="10"
                  value={Math.round(zoom * 100)}
                  onChange={(event) => changeZoom(Number(event.target.value) / 100)}
                  className="w-36"
                  aria-label="Zoom"
                />
                <button
                  type="button"
                  onClick={() => changeZoom(zoom + 0.1)}
                  className="h-9 w-9 rounded-md border border-gray-400 bg-white text-lg font-semibold text-gray-800 hover:bg-gray-50"
                  title="Zoom in"
                >
                  +
                </button>
              </div>
              <button
                type="button"
                onClick={() => changeZoom(1)}
                className="rounded-md border border-gray-400 bg-white px-3 py-2 text-sm font-semibold text-gray-800 hover:bg-gray-50"
              >
                {Math.round(zoom * 100)}%
              </button>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setSnapEnabled((current) => !current)}
                  className={`rounded-md border px-3 py-2 text-sm font-semibold ${
                    snapEnabled ? 'border-blue-600 bg-blue-50 text-blue-800' : 'border-gray-400 bg-white text-gray-800 hover:bg-gray-50'
                  }`}
                >
                  Snap
                </button>
                <select
                  value={snapStepPercent}
                  onChange={(event) => setSnapStepPercent(Number(event.target.value))}
                  className="rounded-md border border-gray-400 bg-white px-2 py-2 text-sm font-semibold text-gray-800"
                  aria-label="Snap spacing"
                >
                  {SNAP_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
              <button
                type="button"
                onClick={() => setShowSampleValues((current) => !current)}
                className={`rounded-md border px-3 py-2 text-sm font-semibold ${
                  showSampleValues ? 'border-blue-600 bg-blue-50 text-blue-800' : 'border-gray-400 bg-white text-gray-800 hover:bg-gray-50'
                }`}
              >
                Values
              </button>
            </div>

            <div
              ref={panSurfaceRef}
              className={`max-h-[78vh] overflow-auto rounded-md bg-gray-300 p-3 select-none ${
                zoom > 1 ? (isPanning ? 'cursor-grabbing' : 'cursor-grab') : ''
              }`}
              onPointerDown={startPan}
              onPointerMove={handlePanMove}
              onPointerUp={stopPan}
              onPointerCancel={stopPan}
              onPointerLeave={stopPan}
            >
              <div
                ref={pageRef}
                className="relative mx-auto bg-white shadow-lg overflow-hidden"
                style={{
                  aspectRatio: pdfPageAspectRatio,
                  width: `${820 * zoom}px`,
                  maxWidth: zoom <= 1 ? '100%' : 'none',
                  backgroundImage: snapEnabled
                    ? `linear-gradient(to right, rgba(37, 99, 235, 0.14) 1px, transparent 1px), linear-gradient(to bottom, rgba(37, 99, 235, 0.14) 1px, transparent 1px)`
                    : undefined,
                  backgroundSize: snapEnabled ? `${snapStepPercent}% ${snapStepPercent}%` : undefined,
                }}
                onPointerMove={handlePointerMove}
                onPointerUp={stopDrag}
                onPointerCancel={stopDrag}
              >
                {pdfImageUrl ? (
                  <img
                    src={pdfImageUrl}
                    alt="PDF template preview"
                    className="absolute inset-0 h-full w-full object-fill pointer-events-none"
                    draggable="false"
                  />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center text-sm text-gray-500">
                    Upload or select a PDF template
                  </div>
                )}

                {snapEnabled && (
                  <div
                    className="absolute inset-0 pointer-events-none"
                    style={{
                      backgroundImage: 'linear-gradient(to right, rgba(37, 99, 235, 0.16) 1px, transparent 1px), linear-gradient(to bottom, rgba(37, 99, 235, 0.16) 1px, transparent 1px)',
                      backgroundSize: `${snapStepPercent}% ${snapStepPercent}%`,
                    }}
                  />
                )}

                {fields.map((field) => {
                  const isSelected = selectedFieldId === field.id;
                  return (
                    <div
                      key={field.id}
                      data-field-box="true"
                      role="button"
                      tabIndex={0}
                      onPointerDown={(event) => startDrag(event, field)}
                      onClick={() => setSelectedFieldId(field.id)}
                      onKeyDown={(event) => handleFieldKeyDown(event, field)}
                      className={`absolute text-left border-2 bg-yellow-100/20 px-1 text-[11px] font-semibold text-gray-900 shadow-sm ${
                        isSelected ? 'border-blue-600 ring-2 ring-blue-200' : 'border-yellow-500'
                      }`}
                      style={{
                        left: `${field.x * 100}%`,
                        top: `${field.y * 100}%`,
                        width: `${field.width * 100}%`,
                        height: `${field.height * 100}%`,
                        cursor: 'move',
                        touchAction: 'none',
                        display: 'flex',
                        alignItems: field.vAlign === 'bottom' ? 'flex-end' : field.vAlign === 'middle' ? 'center' : 'flex-start',
                      }}
                    >
                      <span
                        className={`block w-full pointer-events-none ${normaliseFieldType(field.fieldType) === 'multiline' ? 'whitespace-pre-wrap overflow-hidden' : 'truncate'}`}
                        style={{
                          textAlign: field.align || 'left',
                          fontFamily: fontCssFamily(field.fontFamily),
                          fontWeight: field.bold ? 700 : 400,
                          fontSize: `${Math.max(6, Number(field.fontSize) || 10) * pdfPointScale}px`,
                          lineHeight: 1.18,
                          color: validHexColour(field.color),
                          transform: `translateY(${-Number(field.textOffsetY || 0) * pdfPointScale}px)`,
                        }}
                      >
                        {previewContentForField(field)}
                      </span>
                      {isSelected && ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'].map((handle) => (
                        <span
                          key={handle}
                          role="presentation"
                          onPointerDown={(event) => startResize(event, field, handle)}
                          className={`absolute h-3 w-3 rounded-full border border-blue-700 bg-white shadow ${
                            handle === 'nw' ? '-left-1.5 -top-1.5 cursor-nwse-resize' :
                            handle === 'n' ? 'left-1/2 -top-1.5 -translate-x-1/2 cursor-ns-resize' :
                            handle === 'ne' ? '-right-1.5 -top-1.5 cursor-nesw-resize' :
                            handle === 'e' ? '-right-1.5 top-1/2 -translate-y-1/2 cursor-ew-resize' :
                            handle === 'se' ? '-right-1.5 -bottom-1.5 cursor-nwse-resize' :
                            handle === 's' ? 'left-1/2 -bottom-1.5 -translate-x-1/2 cursor-ns-resize' :
                            handle === 'sw' ? '-left-1.5 -bottom-1.5 cursor-nesw-resize' :
                            '-left-1.5 top-1/2 -translate-y-1/2 cursor-ew-resize'
                          }`}
                        />
                      ))}
                    </div>
                  );
                })}
              </div>
            </div>
          </section>

          <aside className="bg-white border border-gray-200 rounded-lg shadow-sm p-4 space-y-4">
            <div className="flex gap-2">
              <button
                type="button"
                onClick={saveTemplate}
                disabled={!selected || loading}
                className="flex-1 rounded-md bg-[#082547] px-3 py-2 text-sm font-semibold text-white hover:bg-[#0a2d5c] disabled:opacity-50"
              >
                Save to Library
              </button>
              <button
                type="button"
                onClick={generateSample}
                disabled={!selected || loading}
                className="flex-1 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-800 hover:bg-gray-50 disabled:opacity-50"
              >
                Sample
              </button>
              <button
                type="button"
                onClick={deleteTemplate}
                disabled={!selected || loading}
                className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm font-semibold text-red-700 hover:bg-red-100 disabled:opacity-50"
              >
                Delete
              </button>
            </div>

            {selectedField ? (
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Merge Field</label>
                  <select
                    value={selectedField.field}
                    onChange={(event) => handleFieldNameChange(event.target.value)}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                  >
                    {groupedFields.map((group) => (
                      <optgroup key={group.key} label={group.label}>
                        {group.fields.map((field) => (
                          <option key={`${group.key}-${field.value}`} value={field.value}>{field.label}</option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                </div>
                <label className="block text-xs font-semibold text-gray-600">
                  Field Type
                  <select
                    value={normaliseFieldType(selectedField.fieldType)}
                    onChange={(event) => updateField(selectedField.id, { fieldType: event.target.value })}
                    className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                  >
                    {FIELD_TYPES.map((type) => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <label className="text-xs font-semibold text-gray-600">
                    Width %
                    <input
                      type="number"
                      min="5"
                      max="95"
                      value={Math.round(selectedField.width * 100)}
                      onChange={(event) => updateField(selectedField.id, { width: clamp(Number(event.target.value) / 100, 0.05, 0.95) })}
                      className="mt-1 w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm"
                    />
                  </label>
                  <label className="text-xs font-semibold text-gray-600">
                    Height %
                    <input
                      type="number"
                      min="2"
                      max="30"
                      value={Math.round(selectedField.height * 100)}
                      onChange={(event) => updateField(selectedField.id, { height: clamp(Number(event.target.value) / 100, 0.02, 0.3) })}
                      className="mt-1 w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm"
                    />
                  </label>
                  <label className="text-xs font-semibold text-gray-600">
                    Size
                    <input
                      type="number"
                      min="6"
                      max="28"
                      value={selectedField.fontSize}
                      onChange={(event) => updateField(selectedField.id, { fontSize: clamp(Number(event.target.value), 6, 28) })}
                      className="mt-1 w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm"
                    />
                  </label>
                  <label className="text-xs font-semibold text-gray-600">
                    Family
                    <select
                      value={selectedField.fontFamily || 'helvetica'}
                      onChange={(event) => updateField(selectedField.id, { fontFamily: event.target.value })}
                      className="mt-1 w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm"
                    >
                      {fontOptions.map((font) => (
                        <option key={font.value} value={font.value}>{font.label}</option>
                      ))}
                    </select>
                  </label>
                  <label className="text-xs font-semibold text-gray-600">
                    Y Offset
                    <input
                      type="number"
                      min="-20"
                      max="20"
                      step="0.5"
                      value={selectedField.textOffsetY || 0}
                      onChange={(event) => updateField(selectedField.id, { textOffsetY: clamp(Number(event.target.value), -20, 20) })}
                      className="mt-1 w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm"
                    />
                  </label>
                  <label className="flex items-center gap-2 self-end rounded-md border border-gray-300 px-3 py-2 text-sm font-semibold text-gray-700">
                    <input
                      type="checkbox"
                      checked={selectedField.bold === true}
                      onChange={(event) => updateField(selectedField.id, { bold: event.target.checked })}
                    />
                    Bold
                  </label>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Text Colour</label>
                  <div className="rounded-md border border-gray-300 bg-white p-2">
                    <div className="mb-2 flex items-center gap-2">
                      <span
                        className="h-7 w-7 rounded border border-gray-300"
                        style={{ backgroundColor: validHexColour(selectedField.color) }}
                        title="Current text colour"
                      />
                      <input
                        type="text"
                        value={selectedField.color || '#111827'}
                        onChange={(event) => updateField(selectedField.id, { color: event.target.value })}
                        className="min-w-0 flex-1 rounded-md border border-gray-300 px-2 py-1.5 font-mono text-xs uppercase"
                        aria-label="Text colour hex value"
                      />
                      <input
                        type="color"
                        value={validHexColour(selectedField.color)}
                        onChange={(event) => updateField(selectedField.id, { color: event.target.value })}
                        className="h-8 w-10 rounded border border-gray-300 bg-white"
                        aria-label="Custom text colour"
                      />
                    </div>
                    <div className="grid grid-cols-8 gap-1">
                      {textColourSwatches.map((swatch) => {
                        const isActive = (selectedField.color || '').toLowerCase() === swatch.value;
                        return (
                          <button
                            key={swatch.value}
                            type="button"
                            onClick={() => updateField(selectedField.id, { color: swatch.value })}
                            className={`h-7 rounded border ${
                              isActive ? 'border-blue-600 ring-2 ring-blue-200' : 'border-gray-300 hover:border-gray-500'
                            }`}
                            style={{ backgroundColor: swatch.value }}
                            title={swatch.label}
                            aria-label={swatch.label}
                          />
                        );
                      })}
                    </div>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Horizontal Alignment</label>
                  <div className="grid grid-cols-3 gap-2">
                    {ALIGN_OPTIONS.map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => updateField(selectedField.id, { align: option.value })}
                        className={`rounded-md border px-2 py-2 text-sm font-semibold ${
                          (selectedField.align || 'left') === option.value
                            ? 'border-blue-600 bg-blue-50 text-blue-800'
                            : 'border-gray-300 bg-white text-gray-800 hover:bg-gray-50'
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Vertical Alignment</label>
                  <div className="grid grid-cols-3 gap-2">
                    {V_ALIGN_OPTIONS.map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => updateField(selectedField.id, { vAlign: option.value })}
                        className={`rounded-md border px-2 py-2 text-sm font-semibold ${
                          (selectedField.vAlign || 'top') === option.value
                            ? 'border-blue-600 bg-blue-50 text-blue-800'
                            : 'border-gray-300 bg-white text-gray-800 hover:bg-gray-50'
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => removeField(selectedField.id)}
                  className="w-full rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm font-semibold text-red-700 hover:bg-red-100"
                >
                  Remove Field
                </button>
              </div>
            ) : (
              <div className="rounded-md border border-dashed border-gray-300 p-4 text-sm text-gray-500">
                Select a field box to edit it.
              </div>
            )}

            <div className="border-t border-gray-200 pt-4">
              <h3 className="text-sm font-semibold text-gray-800 mb-2">Placed Fields</h3>
              <div className="space-y-2 max-h-64 overflow-auto pr-1">
                {fields.map((field) => (
                  <button
                    key={field.id}
                    type="button"
                    onClick={() => setSelectedFieldId(field.id)}
                    className={`w-full rounded-md border px-3 py-2 text-left text-sm ${
                      selectedFieldId === field.id ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-white hover:bg-gray-50'
                    }`}
                  >
                    <span className="block truncate font-semibold text-gray-800">{field.label}</span>
                    <span className="text-xs text-gray-500">{Math.round(field.x * 100)}%, {Math.round(field.y * 100)}% · {field.align || 'left'} · {field.vAlign || 'top'}</span>
                  </button>
                ))}
                {!fields.length && <p className="text-sm text-gray-500">No fields placed yet.</p>}
              </div>
            </div>
          </aside>
        </section>
      </main>
    </div>
  );
}

export default PdfOverlayTemplatesPage;
